import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState/EmptyState';
import Footer from '../../components/Footer/Footer';
import Menu from '../../components/Menu/Menu';
import Pagination from '../../components/Pagination/Pagination';
import Toast from '../../components/Toast/Toast';
import {
    deleteAutomation,
    getAutomations,
    startAutomation,
    stopAllAutomations,
    stopAutomation,
} from '../../services/AutomationsService';
import {
    alertSuccess,
    confirmAction,
    confirmDelete,
} from '../../utils/confirmDialog';
import AutomationModal from './AutomationModal/AutomationModal';
import AutomationRow from './AutomationRow';
import GridModal from './GridModal/GridModal';
import NewAutomationButton from './NewAutomationButton';
import StopAllAutomationsButton from './StopAllAutomationsButton';

function Automations() {
    const navigate = useNavigate();

    const defaultLocation = useLocation();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    useEffect(() => {
        setPage(getPage(defaultLocation));
    }, [defaultLocation]);

    const [automations, setAutomations] = useState([]);

    const [count, setCount] = useState(0);

    const [notification, setNotification] = useState({ type: '', text: '' });

    const DEFAULT_AUTOMATION = {
        symbol: '',
        conditions: '',
        actions: [],
        name: '',
        indexes: '',
    };

    const [editAutomation, setEditAutomation] = useState(DEFAULT_AUTOMATION);

    const [page, setPage] = useState(getPage());

    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        getAutomations(page || 1)
            .then(result => {
                setAutomations(result.rows);
                setCount(result.count);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }, [page, refresh]);

    function onRefreshClick() {
        setRefresh(Date.now());
    }

    function onViewClick(event) {
        const id = event.target.id.replace('view', '');
        navigate('/automations/' + id);
    }

    function onEditClick(event) {
        const id = event.target.id.replace('edit', '');
        const automation = automations.find(m => m.id == id);

        if (automation.schedule) navigate('/automations/launch/' + id);
        else setEditAutomation({ ...automation });
    }

    function getCopyName(name) {
        const existingNames = automations.map(m => m.name);
        let copyName = `${name} (copy)`;
        let copyNumber = 2;
        while (existingNames.includes(copyName))
            copyName = `${name} (copy ${copyNumber++})`;
        return copyName;
    }

    function onCopyClick(event) {
        const id = event.currentTarget.id.replace('copy', '');
        const automation = automations.find(m => m.id == id);
        if (!automation || automation.schedule) return;

        setEditAutomation({
            ...automation,
            id: 0,
            isActive: false,
            name: getCopyName(automation.name),
        });
    }

    function onStopClick(event) {
        const id = event.target.id.replace('stop', '');
        confirmAction({
            text: 'This automation is running and will be stopped!',
            confirmButtonText: 'Yes, stop it!',
        }).then(confirmed => {
            if (!confirmed) return;
            stopAutomation(id)
                .then(automation => {
                    setRefresh(Date.now());
                })
                .catch(err => {
                    console.error(
                        err.response ? err.response.data : err.message,
                    );
                    setNotification({
                        type: 'error',
                        text: err.response ? err.response.data : err.message,
                    });
                });
        });
    }

    function onStartClick(event) {
        const id = event.target.id.replace('start', '');
        startAutomation(id)
            .then(automation => {
                setRefresh(Date.now());
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onDeleteClick(event) {
        const id = event.target.id.replace('delete', '');
        confirmDelete('This automation will be permanently deleted!').then(
            confirmed => {
                if (!confirmed) return;
                deleteAutomation(id)
                    .then(automation => {
                        setRefresh(Date.now());
                    })
                    .catch(err => {
                        console.error(
                            err.response ? err.response.data : err.message,
                        );
                        setNotification({
                            type: 'error',
                            text: err.response
                                ? err.response.data
                                : err.message,
                        });
                    });
            },
        );
    }

    function onAutomationSubmit(automation) {
        setRefresh(Date.now());
    }

    function onNewAutomationClick(event) {
        if (event.target.id === 'linkSchedule')
            setEditAutomation({
                ...DEFAULT_AUTOMATION,
                schedule: new Date().toISOString(),
            });
        else setEditAutomation(DEFAULT_AUTOMATION);
    }

    function onStopAllAutomationsClick() {
        confirmAction({
            text: 'All automations will be stopped!',
            confirmButtonText: 'Yes, stop all!',
        }).then(confirmed => {
            if (!confirmed) return;
            stopAllAutomations()
                .then(result => {
                    alertSuccess({
                        title: 'Stopped!',
                        text: 'All automations stopped.',
                    }).then(() => {
                        setRefresh(Date.now());
                    });
                })
                .catch(err => {
                    console.error(
                        err.response ? err.response.data : err.message,
                    );
                    setNotification({
                        type: 'error',
                        text: err.response ? err.response.data : err.message,
                    });
                });
        });
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h2 className="h4">Automations</h2>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <div className="d-inline-flex align-items-center">
                            <button
                                type="button"
                                className="btn btn-gray-800 d-inline-flex align-items-center me-2"
                                onClick={onRefreshClick}
                                title="Reload the automations list"
                            >
                                <FontAwesomeIcon
                                    icon={faRedo}
                                    className="icon icon-xs me-2"
                                />
                                Refresh
                            </button>
                            <StopAllAutomationsButton
                                onClick={onStopAllAutomationsClick}
                            />
                            <button
                                id="btnBacktestButton"
                                className="btn btn-primary me-2"
                                title="Backtests"
                                onClick={() =>
                                    navigate('/automations/backtests')
                                }
                            >
                                <svg
                                    className="icon icon-xs"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                                </svg>
                            </button>
                            <NewAutomationButton
                                onClick={onNewAutomationClick}
                            />
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th className="border-gray-200">Symbol</th>
                                <th className="border-gray-200">Name</th>
                                <th className="border-gray-200">Indexes</th>
                                <th className="border-gray-200">Status</th>
                                <th className="border-gray-200">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {automations && automations.length ? (
                                automations.map(automation => (
                                    <AutomationRow
                                        key={automation.id}
                                        data={automation}
                                        onEditClick={onEditClick}
                                        onCopyClick={onCopyClick}
                                        onViewClick={onViewClick}
                                        onStartClick={onStartClick}
                                        onStopClick={onStopClick}
                                        onDeleteClick={onDeleteClick}
                                    />
                                ))
                            ) : (
                                <EmptyState
                                    colSpan={5}
                                    title="No automations yet"
                                    description="Create your first automation to start trading on your own rules."
                                    actionLabel="Create your first automation"
                                    onAction={() =>
                                        setEditAutomation(DEFAULT_AUTOMATION)
                                    }
                                    actionAttrs={{
                                        'data-bs-toggle': 'modal',
                                        'data-bs-target': '#modalAutomation',
                                    }}
                                />
                            )}
                        </tbody>
                    </table>
                    <Pagination count={count} />
                </div>
                <Footer />
            </main>
            <AutomationModal
                data={editAutomation}
                onSubmit={onAutomationSubmit}
            />
            <GridModal data={editAutomation} onSubmit={onAutomationSubmit} />
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default Automations;
